/* DerbyLys – global theme logic: cart drawer, header, localization. No dependencies. */
(() => {
  'use strict';
  window.PG = window.PG || {};

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------- Focus trap helper ---------- */
  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- Cart ---------- */
  const Cart = {
    drawer: null,
    lastTrigger: null,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (this.drawer && this.drawer.__pgBound) return;
      if (this.drawer) this.drawer.__pgBound = true;

      if (!this.docBound) document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener && this.drawer) { e.preventDefault(); this.open(opener); return; }
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); }
      });
      this.docBound = true;

      if (!this.drawer) return;

      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        trapFocus(this.drawer.querySelector('.drawer__panel'), e);
      });

      this.drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-line-change]');
        if (!btn) return;
        e.preventDefault();
        this.change(Number(btn.dataset.line), Number(btn.dataset.quantity));
      });

      this.drawer.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-input]');
        if (!input) return;
        const q = Math.max(0, parseInt(input.value, 10) || 0);
        this.change(Number(input.dataset.line), q);
      });
    },

    sectionIds() {
      return ['cart-drawer'];
    },

    open(trigger) {
      if (!this.drawer) { window.location.href = window.PG.routes.cart; return; }
      this.lastTrigger = trigger || document.activeElement;
      this.drawer.hidden = false;
      this.drawer.removeAttribute('inert');
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        document.documentElement.classList.add('is-locked');
        const focusTarget = this.drawer.querySelector('[data-drawer-focus]') || this.drawer.querySelector(FOCUSABLE);
        focusTarget && focusTarget.focus({ preventScroll: true });
      });
    },

    close() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('is-locked');
      this.drawer.setAttribute('inert', '');
      setTimeout(() => { if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true; }, 320);
      if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus({ preventScroll: true });
    },

    async change(line, quantity) {
      const body = this.drawer.querySelector('[data-cart-body]');
      body && body.classList.add('is-busy');
      try {
        const res = await fetch(`${window.PG.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname }),
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message);
        this.renderSections(json.sections);
        const focus = this.drawer.querySelector(`[data-line-input][data-line="${line}"]`) || this.drawer.querySelector('[data-drawer-focus]');
        focus && focus.focus({ preventScroll: true });
      } catch (err) {
        const msg = this.drawer.querySelector('[data-cart-error]');
        if (msg) { msg.textContent = err.message || (window.PG.strings && window.PG.strings.cartError) || ''; msg.hidden = false; }
      } finally {
        const b = this.drawer.querySelector('[data-cart-body]');
        b && b.classList.remove('is-busy');
      }
    },

    renderSections(sections) {
      if (!sections) return;
      const html = sections['cart-drawer'];
      if (!html || !this.drawer) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const countEl = doc.querySelector('[data-cart-count-source]');
      const count = countEl ? Number(countEl.dataset.cartCountSource) : null;
      const fresh = doc.querySelector('[data-cart-drawer-inner]');
      const current = this.drawer.querySelector('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      if (count !== null) this.setCount(count);
    },

    setCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = n;
      });
      document.querySelectorAll('.cart-btn[aria-label]').forEach((btn) => {
        btn.setAttribute('aria-label', btn.getAttribute('aria-label').replace(/\(\d+\)$/, `(${n})`));
      });
    },
  };
  window.PG.cart = Cart;

  /* ---------- Mobile menu ---------- */
  function initMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const panel = document.querySelector('[data-menu-panel]');
    if (!toggle || !panel) return;
    const set = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      document.documentElement.classList.toggle('is-menu-open', open);
    };
    toggle.addEventListener('click', () => set(toggle.getAttribute('aria-expanded') !== 'true'));
    panel.addEventListener('click', (e) => { if (e.target.closest('a')) set(false); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { set(false); toggle.focus(); } });
    window.matchMedia('(min-width: 900px)').addEventListener('change', (mq) => { if (mq.matches) set(false); });
  }

  /* ---------- Localization auto-submit ---------- */
  function initLocalization() {
    document.addEventListener('change', (e) => {
      const sel = e.target.closest('[data-autosubmit]');
      if (sel && sel.form) sel.form.submit();
    });
  }

  /* ---------- Anchor offset for sticky header ---------- */
  function initAnchors() {
    const header = document.querySelector('[data-header]');
    const update = () => {
      const h = header ? header.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--pg-header-h', `${Math.round(h)}px`);
    };
    update();
    window.addEventListener('resize', update, { passive: true });
  }

  /* ---------- Carousels (reviews) ---------- */
  function initCarousels(root) {
    (root || document).querySelectorAll('[data-carousel]').forEach((c) => {
      if (c.__pgCarousel) return;
      const track = c.querySelector('[data-carousel-track]');
      if (!track) return;
      c.__pgCarousel = true;
      const prev = c.querySelector('[data-carousel-prev]');
      const next = c.querySelector('[data-carousel-next]');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const step = () => {
        const item = track.querySelector('[data-carousel-item]');
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        c.classList.toggle('is-static', max <= 0);
      };
      const go = (dir) => track.scrollBy({ left: dir * step(), behavior: reduce ? 'auto' : 'smooth' });
      prev && prev.addEventListener('click', () => go(-1));
      next && next.addEventListener('click', () => go(1));
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      });
      let raf = 0;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  const boot = () => {
    initCarousels();
    Cart.init();
    initMenu();
    initLocalization();
    initAnchors();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-cart-drawer]')) Cart.init();
    if (e.target.querySelector('[data-menu-toggle]')) initMenu();
    initCarousels(e.target);
  });
})();
